"use client";
import { useState, useEffect } from "react";
import * as React from "react";
import { Heading } from "../common/typography/Heading";
import Typography from "../common/typography";

export interface Heading {
  h2: string;
  h2Slug: string;
  h3s: { h3: string; h3Slug: string }[];
}
interface TableOfContentsProps {
  headings: Heading[];
}

const TableOfContents: React.FC<TableOfContentsProps> = ({ headings }) => {
  const [activeSlug, setActiveSlug] = useState("");
  const [openH2, setOpenH2] = useState<string | null>(null);

  useEffect(() => {
    const allSlugs = headings.flatMap((h2Group) => [
      h2Group.h2Slug,
      ...h2Group.h3s.map((h3) => h3.h3Slug),
    ]);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.id;
            setActiveSlug(id);

            const parentH2 = headings.find(
              (h2Group) =>
                h2Group.h2Slug === id ||
                h2Group.h3s.some((h3) => h3.h3Slug === id)
            );

            if (parentH2) {
              setOpenH2(parentH2.h2Slug);
            }
          }
        });
      },
      {
        rootMargin: "0px 0px -70% 0px",
        threshold: 0,
      }
    );

    allSlugs.forEach((slug) => {
      const element = document.getElementById(slug);
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      allSlugs.forEach((slug) => {
        const element = document.getElementById(slug);
        if (element) {
          observer.unobserve(element);
        }
      });
    };
  }, [headings]);

  const handleH2Click = (slug: string, event: React.MouseEvent) => {
    // event.preventDefault(); // Prevent default anchor jump on click for manual toggle
    setOpenH2(openH2 === slug ? null : slug);
  };

  return (
    <aside data-testid={`blog-table-of-content-block`} className="bg-bgColor max-w-[19.0625rem] gap-5 h-fit sticky  top-stickyTop px-7 py-10 rounded-2xl">
      <Heading size="h6" as="p">
        Table of Contents
      </Heading>
      <nav>
        <ul className="space-y-2.5">
          {headings.map((h2Group, index) => (
            <li key={`h2-${index}`}>
              <a
                data-testid={`blog-table-of-content-block-content-${index}`}
                href={`#${h2Group.h2Slug}`}
                onClick={(e) => handleH2Click(h2Group.h2Slug, e)} // Pass event to prevent default
                className={`text-lg flex  font-semibold items-baseline gap transition-colors duration-200 gap-1 ${activeSlug === h2Group.h2Slug || openH2 === h2Group.h2Slug
                  ? "text-main hover:text-main"
                  : "text-black hover:text-mainHover"
                  }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  className={`shrink-0 transition-transform duration-300 ${openH2 === h2Group.h2Slug ? "rotate-90" : ""
                    }`}
                >
                  <path
                    d="M5.52344 9.94531L8.46875 7L5.52344 4.05469"
                    stroke="#222429"
                    stroke-width="1.4"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <Typography size="body1">{h2Group.h2}</Typography>
                {/* <Typography dangerouslySetInnerHTML={{ __html: h2Group.h2 }}></Typography> */}
              </a>
              {/* Animated wrapper for the H3 list */}
              <div
                style={{
                  maxHeight: openH2 === h2Group.h2Slug ? "500px" : "0", // Adjust 500px if your H3 lists are very long
                  overflow: "hidden",
                  transition: "max-height 0.3s ease-in-out", // Animation properties
                }}
              >
                {h2Group.h3s.length > 0 && (
                  <ul className="ml-4 mt-2 space-y-1 pl-3">
                    {h2Group.h3s.map((h3, h3Index) => (
                      <li key={`h3-${h3Index}`}>
                        <a
                           data-testid={`blog-table-of-content-block-sub-content-${h3Index}`}
                          href={`#${h3.h3Slug}`}
                          className={`block transition-colors duration-200 ${activeSlug === h3.h3Slug
                            ? "text-main hover:text-main font-medium"
                            : "text-gray-600 dark:text-gray-400 hover:text-main"
                            }`}
                        >
                          <Typography size="body1">
                            {h3.h3}
                          </Typography>

                          {/* <Typography dangerouslySetInnerHTML={{ __html: h3.h3 }}></Typography> */}
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </li>
          ))}
        </ul>
      </nav>
    </aside>
  );
};

export default TableOfContents;
